import { useMemo, useState } from 'react'
import firebase from '../../firebase/compat'
import { Link } from 'react-router-dom'
import { client } from '../../hooks/Client'
import useOpenMatches from '../../hooks/useOpenMatches'
import usePersonaTerms from '../../hooks/usePersonaTerms'
import useSharedCollection from '../../firebase/useSharedCollection'
import { useFirestoreQuery } from '../../firebase/useFirestore'
import rankPersonaCandidates, { suggestedPersonaId } from '../../helpers/personaMatching/rankPersonaCandidates'
import { PERSONA_STATUS_LABELS } from '../../helpers/personas/personaStatusLabels'
import { invalidateResearchAggregate } from '../../hooks/measurementSignals/researchAggregateCache'
import StaticCardLabel from './StaticCardLabel'

// De kaart 'Deelnemers om te koppelen': invullingen met de naamstap
// (Research.AskNameFirst) die wachten tot een medewerker ze aan een deelnemer
// koppelt. Nodig naast de adreskaart omdat een naam nooit vanzelf koppelt
// (besluit 6): élke invulling komt hier, met een voorstel uit de
// naamvergelijking bovenaan. Deelt de opmaak van de adreskaart (address-match-*).
// AI-CONTEXT: See docs/patterns.md#dashboard-static-card
// AI-CONTEXT: See docs/features/deelnemer-koppeling-algemene-link.md

/**
 * @typedef {object} PersonaMatch
 * @property {string} ID - Docid, `<CompagnyID>__<SessionID>`
 * @property {string} FirstName
 * @property {string} LastName
 * @property {string} ResearchID
 * @property {string} MomentID
 * @property {string} [PersonaID] - Gevuld terwijl het geval nog open is: een eerdere poging stopte halverwege
 */

/**
 * @typedef {object} ParticipantOption
 * @property {string} id - Personas.ID
 * @property {string} label - Naam, met de status erachter als iemand niet actief is
 */

/**
 * Een deelnemer zonder naam heet hier wat hij elders heet ('Naamloze deelnemer',
 * zoals DashboardStarredCard): een lege optie is onkiesbaar (testronde 2026-10-13, B10).
 * @param {{ID: string, Name?: string, Status?: string}} persona
 * @param {import('../../helpers/personaTerms').PersonaTerms} terms
 * @returns {string}
 */
const optionLabel = (persona, terms) => {
    const name = (persona.Name || '').trim() || `Naamloze ${terms.singularLower}`
    return persona.Status && persona.Status !== 'active' && PERSONA_STATUS_LABELS[persona.Status]
        ? `${name} (${PERSONA_STATUS_LABELS[persona.Status]})`
        : name
}

/**
 * Eén invulling met zijn keuzes. Een eigen component (niet in de render van de
 * kaart gedefinieerd) zodat elke rij zijn eigen keuze en foutmelding houdt.
 * AI-CONTEXT: See docs/pitfalls.md#state-component-defined-in-render
 *
 * @param {object} props
 * @param {PersonaMatch} props.match - Het PersonaMatches-document
 * @param {Array<object>|undefined} props.participants - Deelnemers van de organisatie, zonder medewerkers; undefined zolang ze laden
 * @param {ParticipantOption[]} props.options - Alle deelnemers als keuze, al gesorteerd (één keer per kaart, niet per rij)
 * @param {string} props.context - Onderzoek en meetmoment, al leesbaar gemaakt
 * @param {string} props.momentTitle - Naam van het meetmoment ('' als onbekend)
 * @param {import('../../helpers/personaTerms').PersonaTerms} props.terms - Hoe deze omgeving deelnemers noemt
 * @param {(personaId: string) => void} props.onResolved - Na een geslaagde keuze; '' bij negeren
 */
const PersonaMatchRow = ({ match, participants, options, context, momentTitle, terms, onResolved }) => {
    // null = nog niets gekozen: dan geldt het voorstel, ook als de deelnemers
    // pas later binnenkomen.
    const [chosen, setChosen] = useState(/** @type {string|null} */ (null))
    const [saving, setSaving] = useState(false)
    const [failed, setFailed] = useState(false)
    // Voor welke deelnemer het opzoeken van een eerdere invulling mislukte.
    const [checkFailedFor, setCheckFailedFor] = useState('')

    const loaded = Array.isArray(participants)
    const candidates = useMemo(
        () => (loaded ? rankPersonaCandidates(match.FirstName, match.LastName, participants) : []),
        [loaded, match.FirstName, match.LastName, participants]
    )
    const suggestionIds = new Set(candidates.map((c) => c.personaId))
    const labelOf = new Map(options.map((o) => [o.id, o.label]))
    /** @type {ParticipantOption[]} */
    const suggested = candidates.map((c) => ({ id: c.personaId, label: labelOf.get(c.personaId) || optionLabel({ ID: c.personaId, Name: c.name, Status: c.status }, terms) }))
    /** @type {ParticipantOption[]} */
    const others = options.filter((o) => !suggestionIds.has(o.id))

    // Half gekoppeld: een eerdere poging legde de deelnemer al vast en stopte
    // halverwege. De server laat dan alleen afmaken met díe deelnemer
    // (/complete 2026-10-14), dus de kaart ook.
    const partial = match.PersonaID || ''
    const selected = partial || (chosen ?? suggestedPersonaId(candidates))

    // Heeft de gekozen deelnemer dit meetmoment al ingevuld? Dan zou koppelen
    // twee invullingen onder één meetmoment zetten, die stil gemiddeld worden
    // (testronde 2026-10-13, B11). Waarschuwen, niet blokkeren (besluit Gijs).
    // De antwoorden van déze invulling hebben nog geen Persona, dus elke treffer
    // is een eerdere. Eén read, alleen met een gekozen deelnemer.
    // Geen `limit: 1`: de hook filtert zacht verwijderde antwoorden pas in de
    // browser, dus één opgehaald verwijderd antwoord zou de waarschuwing wegdrukken
    // naast een levend (/complete 2026-10-14). Het gaat om de antwoorden van één
    // deelnemer op één meetmoment: hooguit enkele tientallen documenten.
    const earlier = useFirestoreQuery('QuestionnairesResponses', {
        where: [['Persona', '==', selected], ['ResearchID', '==', match.ResearchID], ['MomentID', '==', match.MomentID]],
        enabled: Boolean(selected),
        initial: '',
        onError: (error) => {
            console.error('Eerdere invulling opzoeken mislukt', error)
            setCheckFailedFor(selected)
        },
    })
    // AI-CONTEXT: See docs/pitfalls.md#ui-empty-state-claims-absence-while-loading
    // AI-CONTEXT: See docs/pitfalls.md#database-firestore-silent-listener-failure
    const checkFailed = Boolean(selected) && checkFailedFor === selected
    const alreadyFilled = Boolean(selected) && !checkFailed && Array.isArray(earlier) && earlier.length > 0

    const choose = async (action) => {
        if (saving) return
        setSaving(true)
        setFailed(false)
        try {
            const resolve = firebase.app().functions('europe-west1').httpsCallable('resolvePersonaMatch')
            const { data } = await resolve({
                client,
                matchId: match.ID,
                action,
                ...(action === 'link' ? { personaId: selected } : {}),
            })
            // Het geval staat daarna op 'resolved' en valt vanzelf uit de lijst
            // bij de volgende snapshot; deze rij verdwijnt dus. Geen state meer.
            onResolved(data?.personaId || '')
        } catch (error) {
            console.error('Invulling koppelen mislukt', error)
            setFailed(true)
            setSaving(false)
        } finally {
            // De antwoorden van dit onderzoek horen nu (deels) bij iemand: de pagina
            // die je hierna opent mag niet de stand van vóór het koppelen tonen (B13).
            // Ook na een fout: de server koppelt in brokken (/complete 2026-10-14).
            invalidateResearchAggregate(match.ResearchID)
        }
    }

    return (
        <li className='address-match-row'>
            <p className='address-match-address'><b>{`${match.FirstName} ${match.LastName}`}</b></p>
            {context && <p className='address-match-reason'>{context}</p>}

            {loaded ? (
                <select
                    aria-label={`Kies een ${terms.singularLower}`}
                    value={selected}
                    onChange={(e) => setChosen(e.target.value)}
                    disabled={saving || Boolean(partial)}
                >
                    <option value=''>{`Kies een ${terms.singularLower}`}</option>
                    {suggested.length > 0 && (
                        <optgroup label='Voorstel'>
                            {suggested.map((o) => <option key={o.id} value={o.id}>{o.label}</option>)}
                        </optgroup>
                    )}
                    {suggested.length > 0 ? (
                        <optgroup label={`Alle ${terms.pluralLower}`}>
                            {others.map((o) => <option key={o.id} value={o.id}>{o.label}</option>)}
                        </optgroup>
                    ) : (
                        others.map((o) => <option key={o.id} value={o.id}>{o.label}</option>)
                    )}
                </select>
            ) : (
                <p className='score-explain-card__placeholder'>Laden…</p>
            )}

            <div className='address-match-actions'>
                <button type='button' disabled={saving || !loaded || !selected} onClick={() => choose('link')}>
                    Koppelen
                </button>
                <button type='button' disabled={saving || Boolean(partial)} onClick={() => choose('create')}>
                    {`Nieuwe ${terms.singularLower}`}
                </button>
                <button type='button' className='button-simple' disabled={saving || Boolean(partial)} onClick={() => choose('ignore')}>
                    Negeren
                </button>
            </div>

            {partial && (
                <p className='measurement-signal-note'>
                    {`Een eerdere koppeling aan deze ${terms.singularLower} stopte halverwege. Klik op 'Koppelen' om hem af te maken.`}
                </p>
            )}

            {checkFailed && (
                <p className='measurement-signal-note'>
                    {`Kon niet nagaan of deze ${terms.singularLower} '${momentTitle || 'dit meetmoment'}' al heeft ingevuld.`}
                </p>
            )}

            {alreadyFilled && (
                <p className='measurement-signal-warning'>
                    {`Deze ${terms.singularLower} heeft '${momentTitle || 'dit meetmoment'}' al ingevuld. Koppel je toch, dan staan er twee invullingen voor dit meetmoment.`}
                </p>
            )}

            {failed && <p className='measurement-signal-warning' role='alert'>Dit lukte niet. Probeer het opnieuw.</p>}
        </li>
    )
}

/**
 * Deelnemers om te koppelen: invullingen met de naamstap die op een medewerker
 * wachten. Elke invulling komt hier; er wordt nooit vanzelf gekoppeld.
 */
const DashboardPersonaMatchesCard = () => {
    const terms = usePersonaTerms()
    const { matches, loaded, error } = useOpenMatches('PersonaMatches')

    // Gedeelde listeners: andere kaarten lezen dezelfde collecties al.
    // Medewerkers (een Persona met UserID) zijn geen deelnemers om aan te koppelen.
    const personas = useSharedCollection(matches.length > 0 ? 'Personas' : '')
    const researches = useSharedCollection(matches.length > 0 ? 'Research' : '')
    const moments = useSharedCollection(matches.length > 0 ? 'MeasureMoments' : '')
    const participants = useMemo(
        () => (Array.isArray(personas) ? personas.filter((p) => !p.UserID) : undefined),
        [personas]
    )
    // Eén keer per kaart sorteren, niet per rij: bij duizenden deelnemers en
    // tientallen open gevallen scheelt dat tientallen sorteringen per snapshot.
    /** @type {ParticipantOption[]} */
    const options = useMemo(
        () => (participants || [])
            .map((p) => ({ id: p.ID, label: optionLabel(p, terms) }))
            .sort((a, b) => a.label.localeCompare(b.label) || a.id.localeCompare(b.id)),
        [participants, terms]
    )

    /** @param {PersonaMatch} match */
    const momentTitleOf = (match) => (moments || []).find((m) => m.ID === match.MomentID)?.Title || ''
    /** @param {PersonaMatch} match */
    const contextOf = (match) => [
        (researches || []).find((r) => r.ID === match.ResearchID)?.Title,
        momentTitleOf(match),
    ].filter(Boolean).join(' · ')

    // Na een keuze verdwijnt de rij; zeg dan waar de deelnemer staat.
    const [lastPersonaId, setLastPersonaId] = useState('')
    const doneNote = lastPersonaId ? (
        <p className='measurement-signal-note'>
            Gekoppeld. <Link to={`/${client}/personabuilder/${lastPersonaId}`}>{`Open de ${terms.singularLower}`}</Link>
        </p>
    ) : null

    const title = <StaticCardLabel title={`${terms.plural} om te koppelen`} pill={{ variant: 'personas', label: terms.plural }} />

    // AI-CONTEXT: See docs/pitfalls.md#ui-empty-state-claims-absence-while-loading
    if (!loaded) {
        return <div>{title}<p className='score-explain-card__placeholder'>Laden…</p></div>
    }

    if (error) {
        return (
            <div>
                {title}
                <p className='measurement-signal-warning' role='alert'>
                    De lijst kon niet worden geladen. Er kunnen invullingen wachten die hier niet staan.
                </p>
            </div>
        )
    }

    if (matches.length === 0) {
        return (
            <div>
                {title}
                {doneNote}
                <p className='measurement-signal-note'>Er wachten geen invullingen op een koppeling.</p>
            </div>
        )
    }

    return (
        <div>
            {title}
            {doneNote}
            <ul className='address-match-list'>
                {matches.map((match) => (
                    <PersonaMatchRow
                        key={match.ID}
                        match={match}
                        participants={participants}
                        options={options}
                        context={contextOf(match)}
                        momentTitle={momentTitleOf(match)}
                        terms={terms}
                        onResolved={setLastPersonaId}
                    />
                ))}
            </ul>
        </div>
    )
}

export default DashboardPersonaMatchesCard
